import { AlertTriangle, ShieldOff, Tag, UserMinus, Users } from "lucide-react";
import { useAppSelector } from "@/stores/hook";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import {
  useLeadInvisibility,
  type LeadInvisibilityReason,
} from "@/features/leads/api/useLeadInvisibility";
import {
  useLeadRevokeSummary,
  type LeadRevokeSummaryEntry,
} from "@/features/leads/api/useLeadRevokeSummary";
import { selectIsTenantAdmin } from "@/rbac/rbacSlice";
import { getRelativeTime } from "@/utils/date";

/**
 * Banner shown at the top of the lead detail panel under
 * Revoked → No Access. Two modes:
 *
 * - Non-admin caller: lists THEIR concrete missing access (revoked
 *   assignment, stage not in role, branch unassigned, CF block, soft
 *   delete). Hidden when the caller actually has access.
 *
 * - Tenant admin: pivots to a "who lost access to this lead?" panel.
 *   The admin always "has access", so the caller-side invisibility
 *   explainer returns nothing actionable here — but on the Revoked
 *   tab they're specifically looking at leads someone else was
 *   revoked from, and the useful question is which user, when, by
 *   whom, and why.
 */
export default function LeadInvisibilityBanner() {
  const { lead } = useActiveDataMutation();
  const leadId = lead?.id;

  const isRevoked = useAppSelector(
    (state) => state.leadFilterSlice.isRevoked,
  );
  const revokeType = useAppSelector(
    (state) => state.leadFilterSlice.revokeType,
  ) as string;
  const isAdmin = useAppSelector(selectIsTenantAdmin);

  const onRevokedTab =
    isRevoked === true && (revokeType === "noAccess" || revokeType === "all");

  const invisibilityEnabled = !!leadId && onRevokedTab && !isAdmin;
  const revokeSummaryEnabled = !!leadId && onRevokedTab && isAdmin;

  const { data: invisibilityData } = useLeadInvisibility(leadId, {
    enabled: invisibilityEnabled,
  });
  const { data: revokeSummary } = useLeadRevokeSummary(leadId, {
    enabled: revokeSummaryEnabled,
  });

  if (!onRevokedTab) return null;

  if (isAdmin) {
    const entries = revokeSummary?.entries ?? [];
    if (entries.length === 0) return null;
    return <AdminRevokePanel entries={entries} />;
  }

  if (!invisibilityData || invisibilityData.hasAccess) return null;
  if (!invisibilityData.reasons.length) return null;

  return (
    <div className="rounded-[12px] border border-[#fbdcdc] bg-[#fef2f2] px-3 py-2.5 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <AlertTriangle className="size-4 text-[#da2a46] shrink-0" />
        <p className="text-[13px] font-medium text-[#7a1c2b] tracking-[-0.26px]">
          You can't fully access this lead. Missing:
        </p>
      </div>
      <ul className="flex flex-col gap-1.5 pl-6">
        {invisibilityData.reasons.map(
          (r: LeadInvisibilityReason, i: number) => (
            <li key={i} className="text-[12px] text-[#5e0f1b] leading-snug">
              <span className="font-medium">{r.label}</span>
              {r.detail ? (
                <span className="text-[#7a1c2b]"> {r.detail}</span>
              ) : null}
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

function AdminRevokePanel({ entries }: { entries: LeadRevokeSummaryEntry[] }) {
  return (
    <div className="rounded-[12px] border border-[#fbdcdc] bg-[#fef2f2] px-3 py-2.5 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <AlertTriangle className="size-4 text-[#da2a46] shrink-0" />
        <p className="text-[13px] font-medium text-[#7a1c2b] tracking-[-0.26px]">
          {entries.length === 1
            ? "1 user was revoked from this lead"
            : `${entries.length} users were revoked from this lead`}
        </p>
      </div>
      <ul className="flex flex-col gap-1.5 pl-1">
        {entries.map((e, i) => (
          <RevokeEntryRow key={i} entry={e} />
        ))}
      </ul>
    </div>
  );
}

const CAUSE_META: Record<
  NonNullable<LeadRevokeSummaryEntry["cause"]>,
  { label: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  cf_access_lost: { label: "Custom-field access removed", Icon: Tag },
  replaced_same_role: {
    label: "Replaced by a same-role assignment",
    Icon: Users,
  },
  manual: { label: "Manually unassigned", Icon: UserMinus },
};

function RevokeEntryRow({ entry }: { entry: LeadRevokeSummaryEntry }) {
  const meta = entry.cause ? CAUSE_META[entry.cause] : null;
  const Icon = meta?.Icon ?? ShieldOff;
  const causeText = meta?.label ?? "Revoked";
  const when = entry.revokedAt ? getRelativeTime(entry.revokedAt) : null;
  const target =
    entry.level === "child" && entry.childName
      ? `Deal "${entry.childName}"`
      : "Lead";
  return (
    <li className="flex items-start gap-2 px-1.5 py-1 rounded-md">
      <span className="mt-0.5 inline-flex items-center justify-center rounded-full bg-white p-1 border border-[#fbdcdc]">
        <Icon className="size-3.5 text-[#7a1c2b]" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium text-[#5e0f1b] leading-snug">
          {entry.userName}
          {entry.roleName ? (
            <span className="text-[#7a1c2b] font-normal"> · {entry.roleName}</span>
          ) : null}
          <span className="text-[#7a1c2b] font-normal"> · {target}</span>
        </p>
        <p className="text-[11px] text-[#7a1c2b] leading-snug mt-0.5">
          {causeText}
          {entry.revokedByName ? ` by ${entry.revokedByName}` : ""}
          {when ? ` · ${when}` : ""}
        </p>
        {entry.message ? (
          <p className="text-[11px] text-[#7a1c2b]/85 leading-snug mt-0.5 italic">
            {entry.message}
          </p>
        ) : null}
      </div>
    </li>
  );
}
